import * as yup from 'yup';
import { ccIntl } from 'helpers/intl-helper';
import { defineMessages } from 'react-intl';
import { CompanySizeOptions } from './fields/CompanySize';

const { formatMessage } = ccIntl;

const messages = defineMessages({
  firstNameLimit: {
    id: 'Onboard.form_validation_first_name_character_limit',
    description: 'Error message for long first name',
    defaultMessage: 'First name must be less than 50 characters',
  },
  firstNameRequired: {
    id: 'Onboard.form_validation_first_name_required',
    description: 'Error message for required first name',
    defaultMessage: 'First name is required',
  },
  lastNameMax: {
    id: 'Onboard.form_validation_last_name_character_limit',
    description: 'Error message for long last name',
    defaultMessage: 'Last name must be less than 50 characters',
  },
  lastNameRequired: {
    id: 'Onboard.form_validation_last_name_required',
    description: 'Error message for required last name',
    defaultMessage: 'Last name is required',
  },
  companyNameMax: {
    id: 'Onboard.form_validation_company_name_character_limit',
    description: 'Error message for long company name',
    defaultMessage: 'Company name must be less than 80 characters',
  },
  companyNameRequired: {
    id: 'Onboard.form_validation_company_name_required',
    description: 'Error message for required company name',
    defaultMessage: 'Company name is required',
  },
  phoneNumberFormat: {
    id: 'Onboard.form_validation_phone_number_invalid',
    description: 'Error message for invalid phone number',
    defaultMessage: 'Phone number is invalid',
  },
  phoneNumberMax: {
    id: 'Onboard.form_validation_phone_number_character_limit',
    description: 'Error message for long phone number',
    defaultMessage: 'Phone number must be less than 50 characters',
  },
  phoneNumberRequired: {
    id: 'Onboard.form_validation_phone_number_required',
    description: 'Error message for required phone number',
    defaultMessage: 'Phone number is required',
  },
  emailFormat: {
    id: 'Onboard.form_validation_email_invalid',
    description: 'Error message for invalid email',
    defaultMessage: 'Email address is invalid',
  },
  emailMax: {
    id: 'Onboard.form_validation_email_character_limit',
    description: 'Error message for long email',
    defaultMessage: 'Email address must be less than 50 characters',
  },
  emailRequired: {
    id: 'Onboard.form_validation_email_required',
    description: 'Error message for required email',
    defaultMessage: 'Email address is required',
  },
  passwordMin: {
    id: 'Onboard.form_validation_password_short',
    description: 'Error message for short password',
    defaultMessage: 'Password must be at least 10 characters',
  },
  passwordMax: {
    id: 'Onboard.form_validation_password_long',
    description: 'Error message for long password',
    defaultMessage: 'Password must be less than 100 characters',
  },
  passwordCharTypes: {
    id: 'Onboard.form_validation_password_char_types',
    description: 'Error message for password character types',
    defaultMessage:
      'Password must have one uppercase letter and one lowercase letter',
  },
  passwordRequired: {
    id: 'Onboard.form_validation_password_required',
    description: 'Error message for required password',
    defaultMessage: 'Password is required',
  },
  industryRequired: {
    id: 'Onboard.form_validation_industry_required',
    description: 'error message for required industry',
    defaultMessage: 'Industry is required',
  },
  roleRequired: {
    id: 'Onboard.form_validation_role_required',
    description: 'error message for required role',
    defaultMessage: 'Role is required',
  },
  fieldRequired: {
    id: 'Onboard.form_validation_field_required',
    description: 'error message for required field',
    defaultMessage: 'This field is required',
  },
});

export default function getCreateAccountValidation({
  isMobile,
  requireCompanySize,
  requireHowHeardDetail,
}) {
  const validationSchema = {
    firstName: yup
      .string()
      .nullable()
      .max(50, formatMessage(messages.firstNameLimit))
      .required(formatMessage(messages.firstNameRequired)),
    lastName: yup
      .string()
      .nullable()
      .max(50, formatMessage(messages.lastNameMax))
      .required(formatMessage(messages.lastNameRequired)),
    companyName: yup
      .string()
      .nullable()
      .max(80, formatMessage(messages.companyNameMax))
      .required(formatMessage(messages.companyNameRequired)),
    phoneNumber: yup
      .string()
      .nullable()
      .matches(
        /^(\+?\d{1,2}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
        formatMessage(messages.phoneNumberFormat),
      )
      .max(50, formatMessage(messages.phoneNumberMax))
      .required(formatMessage(messages.phoneNumberRequired)),
    email: yup
      .string()
      .nullable()
      .matches(
        /^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/,
        formatMessage(messages.emailFormat),
      )
      .max(50, formatMessage(messages.emailMax))
      .required(formatMessage(messages.emailRequired)),
    password: yup
      .string()
      .nullable()
      .min(10, formatMessage(messages.passwordMin))
      .max(100, formatMessage(messages.passwordMax))
      .matches(/^(?=.*[A-Z])(?=.*[a-z]).*$/, {
        message: formatMessage(messages.passwordCharTypes),
        excludeEmptyString: true,
      })
      .required(formatMessage(messages.passwordRequired)),
    acceptedTos: yup.boolean().required(),
    industry: isMobile
      ? null
      : yup.string().required(formatMessage(messages.industryRequired)),
    howHeardType: yup.string().nullable().required(),
    howHeardDetail: yup.string().nullable(),
    companySize: null,
    primaryUserTitle: yup
      .string()
      .required(formatMessage(messages.roleRequired)),
  };

  if (requireCompanySize) {
    const validSizes = CompanySizeOptions.map((size) => size.value);
    validationSchema['companySize'] = yup.string().oneOf(validSizes).required();
  }

  if (requireHowHeardDetail) {
    validationSchema['howHeardDetail'] = yup
      .string()
      .nullable()
      .required(formatMessage(messages.fieldRequired));
  }

  return yup.object().shape(validationSchema);
}
